/**
 * The text a result card shows for a run of fields, joined in the order asked for and separated
 * by commas unless `noCommas` - names read as "Ada Lovelace", addresses as "London, England".
 * A field with no value is left out, and when the last one is missing the comma trailing the
 * field before it goes with it.
 */
export function getText(fieldNames: string[], base: unknown, noCommas: boolean = false): string {
  let text: string = "";

  for (let index = 0; index < fieldNames.length; index++) {
    const value = fieldNames[index];
    const isLastLoop: boolean = index === fieldNames.length - 1;
    const comma: string = noCommas || isLastLoop ? "" : ",";

    // If value is invalid, and we're on the last iteration then remove the last comma
    if (!base?.[value as keyof unknown] && isLastLoop) text = text.slice(0, -1);

    // Value is invalid, skip
    if (!base?.[value as keyof unknown]) continue;

    text += ` ${base?.[value as keyof unknown]}${comma}`;
  }

  return text.trim();
}

/**
 * The letters a result's avatar shows when it has no picture: the first letter of the first two
 * words that start with a letter or digit, so "Halvorsen & Pike LLP" reads "HP" rather than "H&".
 */
export function getResultInitials(name: string): string {
  const words = name.split(/\s+/).filter((word) => /^[\p{L}\p{N}]/u.test(word));
  return words
    .slice(0, 2)
    .map((word) => Array.from(word)[0]!.toLocaleUpperCase())
    .join("");
}

/**
 * How a list-valued fact reads on one line: the first `shown` items, and a count of the rest that the
 * card draws as "+N". Blank entries are dropped first, so they neither show nor count.
 */
export function splitFactList(items: readonly (string | null | undefined)[], shown: number): {
  listed: string[];
  hiddenCount: number;
} {
  const present = items.map((item) => item?.trim()).filter((item): item is string => Boolean(item));
  return { listed: present.slice(0, shown), hiddenCount: Math.max(0, present.length - shown) };
}

type ResultLocation = { city?: string | null; state?: string | null; country?: string | null } | string | null | undefined;

/**
 * Where a result is, as a card says it: the town and state, not the street. The full address belongs
 * on the profile. Falls back to the country when neither is known.
 */
export function getCityAndStateText(location: ResultLocation): string {
  if (!location) return "";
  if (typeof location === "string") return location;
  const cityAndState = [location.city, location.state].filter(Boolean).join(", ");
  return cityAndState || (location.country ?? "");
}

/** A whole-dollar amount, as "$2,000,000": an insured amount has no cents worth showing on a card. */
export function getWholeDollarText(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount) || amount <= 0) return "";
  return new Intl.NumberFormat("en-US", { currency: "USD", maximumFractionDigits: 0, style: "currency" }).format(
    amount,
  );
}

/** "120 drafted · 85 allowed", or whichever half the profile gives. A zero is not worth a line. */
export function getApplicationsText(drafted: number | null | undefined, allowed: number | null | undefined): string {
  return [drafted ? `${drafted} drafted` : "", allowed ? `${allowed} allowed` : ""].filter(Boolean).join(" · ");
}

/**
 * Several people's lists as one, in the order first seen, each value once however it is cased. A
 * team's card shows what its people cover between them.
 */
export function mergeFactLists(lists: readonly (readonly (string | null | undefined)[] | null | undefined)[]): string[] {
  const merged = new Map<string, string>();
  for (const value of lists.flatMap((list) => list ?? [])) {
    const trimmed = value?.trim();
    if (trimmed && !merged.has(trimmed.toLowerCase())) merged.set(trimmed.toLowerCase(), trimmed);
  }
  return [...merged.values()];
}
